import { Button, StickyBottomActions } from "../../design-v2";
import {
  AREA_KIND_MAP,
  AREA_PARENT_MAP,
} from "../../features/settings/settingsOptions";
// Phase 10B promoted the accepted onboarding result presentation into one shared
// product component; Phase 11B completed the ownership move by renaming the
// classes to `lz-generated-program-result-*` and relocating their rules,
// verbatim, from `features/onboarding/onboarding.css` into the shared product
// stylesheet. The rendered result is unchanged on both surfaces.
import "./product.css";
import { toMuscleVolumeItems } from "../../features/muscleVolume/plannedMuscleVolume";
import { formatSetCount } from "../../features/insights/domain/insightsPresentation";

const DISPLAY_PRIORITY_PARENT = {
  upper_chest: "chest",
  mid_chest: "chest",
  lower_chest: "chest",
  lats: "back",
  upper_back: "back",
  lower_back: "back",
  front_delts: "shoulders",
  side_delts: "shoulders",
  rear_delts: "shoulders",
  biceps_long_head: "biceps",
  biceps_short_head: "biceps",
  triceps_long_head: "triceps",
  triceps_lateral_head: "triceps",
  upper_abs: "abs",
  lower_abs: "abs",
  obliques: "abs",
  glute_max: "glutes",
  glute_med: "glutes",
  gastrocnemius: "calves",
  soleus: "calves",
};

function normalizeWords(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

export function resolveDisplayPriority(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return DISPLAY_PRIORITY_PARENT[normalized] ||
    (AREA_KIND_MAP[normalized] === "micro"
      ? AREA_PARENT_MAP[normalized]
      : null) ||
    normalized;
}

export function buildPrioritizedMuscleKeys(profile) {
  const priorities = profile?.musclePriorities || {};
  const values = [
    priorities.primaryFocus,
    ...(Array.isArray(priorities.secondaryFocuses)
      ? priorities.secondaryFocuses
      : []),
  ];
  const result = new Set();
  values.filter(Boolean).forEach((value) => {
    const macro = resolveDisplayPriority(value);
    result.add(macro);
    if (macro === "abs" || macro === "core") {
      result.add("abs");
      result.add("core");
    }
    // The canonical muscle-volume group is `quads`.
    if (macro === "quadriceps") {
      result.add("quads");
    }
  });
  return result;
}

function normalizeDistributionKey(entry) {
  const value = entry?.key || entry?.label;
  return normalizeWords(value)
    .toLowerCase()
    .replace(/\s*&\s*/g, "_")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function isDisplayMetric(value) {
  return Number.isFinite(Number(value)) && Number(value) > 0;
}

function formatIdentifier(value) {
  const normalized = String(value || "").replaceAll("_", " ").trim();
  return normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : "";
}

function MetricTile({ value, label, accent = false, suffix = "" }) {
  return (
    <div className="lz-generated-program-result-metric">
      <dt>{label}</dt>
      <dd className={accent ? "lz-generated-program-result-metric__value--accent" : ""}>
        {value}{suffix}
      </dd>
    </div>
  );
}

function MuscleVolumeRow({ entry, accent = false, legacy = false }) {
  const width = Math.max(0, Math.min(100, Number(entry.percentage) || 0));
  return (
    <div className="lz-generated-program-result-muscle">
      <div>
        <span>{entry.label || entry.key}</span>
        <strong className={accent ? "lz-generated-program-result-muscle__value--info" : ""}>
          {legacy
            ? `${entry.rawSets} sets`
            : `${formatSetCount(entry.weightedSets)} weighted ${Number(entry.weightedSets) === 1 ? "set" : "sets"}`}
        </strong>
      </div>
      <span className="lz-generated-program-result-muscle__track" aria-hidden="true">
        <span
          className={accent ? "lz-generated-program-result-muscle__fill--info" : ""}
          style={{ width: `${width}%` }}
        />
      </span>
    </div>
  );
}

function formatScheduledDay(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "";
}

function TrainingSplitRow({ entry }) {
  const day = formatScheduledDay(entry.scheduledDay);
  return (
    <li className="lz-generated-program-result-split-row">
      {day ? <span>{day}</span> : null}
      <strong>{entry.name}</strong>
    </li>
  );
}

function CoachInsightCard({ title, children, tone = "action" }) {
  return (
    <article className="lz-generated-program-result-insight">
      <span
        className={`lz-generated-program-result-insight__mark lz-generated-program-result-insight__mark--${tone}`}
        aria-hidden="true"
      >
        {tone === "info" ? "↗" : "◇"}
      </span>
      <div>
        <h3>{title}</h3>
        <div>{children}</div>
      </div>
    </article>
  );
}

// Onboarding converts its generated weekly plan into a Cycle, so its split is
// the Cycle's scheduled week-one workouts. The AI Weekly Plan Builder generates
// a Weekly Plan only: there is no Cycle and therefore no scheduled weekday, so
// the plan's own `presentation.weeklyStructure` is the truthful list source.
function buildTrainingSplit({ mode, cycle, presentation }) {
  if (mode === "weekly-plan") {
    return (Array.isArray(presentation.weeklyStructure)
      ? presentation.weeklyStructure
      : []
    )
      .map((value, index) => ({
        key: `structure-${index}`,
        name: formatIdentifier(value),
      }))
      .filter((entry) => entry.name);
  }

  return (Array.isArray(cycle?.builderPayload?.weeks?.[0]?.workouts)
    ? [...cycle.builderPayload.weeks[0].workouts]
    : []
  )
    .filter((workout) => workout?.name)
    .sort((left, right) => Number(left.orderIndex) - Number(right.orderIndex))
    .map((workout) => ({
      key: workout.id || workout.orderIndex,
      name: workout.name,
      scheduledDay: workout.scheduledDay,
    }));
}

export default function GeneratedProgramResult({
  mode = "onboarding",
  weeklyPlan,
  cycle,
  profile,
  onModify,
  onDetails,
  isModifyPending = false,
  actionError = "",
}) {
  const isWeeklyPlanMode = mode === "weekly-plan";
  const presentation = weeklyPlan?.presentation || {};
  const metrics = weeklyPlan?.metrics || {};
  const name = presentation.title || weeklyPlan?.name || "Your Program";
  const prioritizedMuscleKeys = buildPrioritizedMuscleKeys(profile);
  const cycleId = isWeeklyPlanMode
    ? null
    : cycle?.cycleId || cycle?.cycle?.id;
  const durationWeeks = isWeeklyPlanMode
    ? Number.NaN
    : Number(cycle?.durationWeeks || cycle?.cycle?.durationWeeks);
  const hasActionTarget = isWeeklyPlanMode
    ? Boolean(weeklyPlan?.weeklyPlanParentId)
    : Boolean(cycleId);
  // Canonical weighted volume (DIRECT = 1, INDIRECT = 0.5, MINOR = 0), the
  // same groups as Insights. Only a historical result that carries no
  // canonical volume (and whose version could not be read back) falls back to
  // its stored bodyParts distribution, explicitly labelled as legacy.
  const isLegacyDistribution = !metrics.weeklyMuscleVolume;
  const muscleVolumeItems = toMuscleVolumeItems(metrics.weeklyMuscleVolume)
    .filter((entry) => entry.weightedSets > 0);
  const maxWeightedSets = Math.max(1, ...muscleVolumeItems.map((entry) => entry.weightedSets));
  const muscleDistribution = isLegacyDistribution
    ? (Array.isArray(metrics.weeklyMuscleDistribution) ? metrics.weeklyMuscleDistribution : [])
      .filter((entry) => Number(entry?.rawSets) > 0)
    : muscleVolumeItems.map((entry) => ({
      ...entry,
      percentage: (entry.weightedSets / maxWeightedSets) * 100,
    }));
  const unclassifiedSets = Number(metrics.weeklyMuscleVolume?.unclassifiedSets) || 0;
  const splitEntries = buildTrainingSplit({ mode, cycle, presentation });
  const coachingNotes = Array.isArray(presentation.coachingNotes)
    ? presentation.coachingNotes.filter(Boolean)
    : [];
  // The legacy AI Builder result rendered constraint guidance as its own V1
  // list. It is truthful coaching information, so it is preserved inside the
  // shared Coach's Insight rather than discarded — and only in Weekly-Plan mode,
  // so the owner-accepted onboarding result is byte-for-byte unchanged.
  const constraintNotes = isWeeklyPlanMode && Array.isArray(presentation.constraintNotes)
    ? presentation.constraintNotes.filter(Boolean)
    : [];
  const hasInsights = Boolean(
    presentation.progression || coachingNotes.length || constraintNotes.length
  );
  const metricItems = [
    { key: "sets", value: metrics.strengthSets, label: "Total Sets", accent: true },
    { key: "exercises", value: metrics.totalExercises, label: "Exercises" },
    { key: "time", value: metrics.averageDurationMinutes, label: "Avg. Workout", suffix: "m" },
    { key: "tut", value: metrics.averageTUTMinutes, label: "Avg. TUT", suffix: "m" },
  ].filter((item) => isDisplayMetric(item.value));

  return (
    <>
      <section
        className="lz-generated-program-result"
        aria-labelledby="generated-program-result-title"
      >
        <header className="lz-generated-program-result-hero">
          <p className="lz-generated-program-result-status">Program Generated</p>
          {Number.isSafeInteger(durationWeeks) && durationWeeks > 0 ? (
            <p className="lz-generated-program-result-duration">{durationWeeks}-Week Training Cycle</p>
          ) : null}
          <h1 id="generated-program-result-title">{name}</h1>
          {presentation.summary ? <p>{presentation.summary}</p> : null}
        </header>

        {metricItems.length > 0 ? (
          <section className="lz-generated-program-result-overview" aria-labelledby="weekly-volume-title">
            <h2 id="weekly-volume-title">Weekly Volume Overview</h2>
            <dl>
              {metricItems.map((item) => <MetricTile key={item.key} {...item} />)}
            </dl>
          </section>
        ) : null}

        {muscleDistribution.length > 0 ? (
          <section className="lz-generated-program-result-section" aria-labelledby="muscle-volume-title">
            <div className="lz-generated-program-result-section__heading">
              <h2 id="muscle-volume-title">
                {isLegacyDistribution ? "Legacy muscle distribution" : "Muscle Volume Distribution"}
              </h2>
              <span>{isLegacyDistribution ? "Weekly Sets" : "Weighted Weekly Sets"}</span>
            </div>
            <div className="lz-generated-program-result-muscles">
              {muscleDistribution.map((entry) => (
                <MuscleVolumeRow
                  key={entry.key || entry.label}
                  entry={entry}
                  legacy={isLegacyDistribution}
                  accent={prioritizedMuscleKeys.has(
                    normalizeDistributionKey(entry)
                  )}
                />
              ))}
            </div>
            <p className="lz-generated-program-result-muscles__note">
              {isLegacyDistribution
                ? "Saved before weighted muscle volume: every listed body part counts each set in full."
                : "Weighted sets: direct = 1, indirect = 0.5."}
              {!isLegacyDistribution && unclassifiedSets > 0
                ? ` ${formatSetCount(unclassifiedSets)} ${unclassifiedSets === 1 ? "set" : "sets"} not counted: muscle data unavailable.`
                : ""}
            </p>
          </section>
        ) : null}

        {splitEntries.length > 0 ? (
          <section className="lz-generated-program-result-section" aria-labelledby="training-split-title">
            <div className="lz-generated-program-result-section__heading">
              <h2 id="training-split-title">Your Training Split</h2>
            </div>
            <ol className="lz-generated-program-result-split">
              {splitEntries.map((entry) => (
                <TrainingSplitRow key={entry.key} entry={entry} />
              ))}
            </ol>
          </section>
        ) : null}

        {hasInsights ? (
          <section className="lz-generated-program-result-section" aria-labelledby="coach-insight-title">
            <div className="lz-generated-program-result-section__heading">
              <h2 id="coach-insight-title">Coach&apos;s Insight</h2>
            </div>
            <div className="lz-generated-program-result-insights">
              {presentation.progression ? (
                <CoachInsightCard title="Progression">
                  <p>{presentation.progression}</p>
                </CoachInsightCard>
              ) : null}
              {coachingNotes.length > 0 ? (
                <CoachInsightCard title="Coaching Notes" tone="info">
                  <ul>
                    {coachingNotes.map((note, index) => <li key={`${note}-${index}`}>{note}</li>)}
                  </ul>
                </CoachInsightCard>
              ) : null}
              {constraintNotes.length > 0 ? (
                <CoachInsightCard title="Training Constraints" tone="info">
                  <ul>
                    {constraintNotes.map((note, index) => (
                      <li key={`${note}-${index}`}>{note}</li>
                    ))}
                  </ul>
                </CoachInsightCard>
              ) : null}
            </div>
          </section>
        ) : null}

        {actionError ? (
          <p role="alert" className="lz-generated-program-result-action-error">
            {actionError}
          </p>
        ) : null}
      </section>

      <StickyBottomActions
        aria-label="Generated program actions"
        className="lz-generated-program-result-actions"
        innerClassName="lz-generated-program-result-actions__inner"
      >
        <Button
          variant="secondary"
          size="lg"
          onClick={onModify}
          disabled={!hasActionTarget || isModifyPending}
        >
          {isModifyPending ? "Opening…" : "Modify"}
        </Button>
        <Button
          size="lg"
          onClick={onDetails}
          disabled={!hasActionTarget || isModifyPending}
        >
          Details
        </Button>
      </StickyBottomActions>
    </>
  );
}
